<template>
  <div class="create-event">
    <span class="create-event__title">{{popupTitle}}</span>
    <label class="create-event__form-group">
      <span class="create-event__label">Event Name:</span>
      <input type="text"
             name="name"
             class="form-control"
             :disabled="isCurrentUserClientCollaborator"
             :class="{'create-event__form-group_invalid': isNameError}"
             placeholder="Enter Name"
             v-model="name">
      <span class="error__message" v-if="isNameError">Required field</span>
    </label>
    <label class="create-event__form-group">
      <span class="create-event__label">Description:</span>
      <div class="form-control create-event__description__wrapper">
      <textarea
        class="create-event__description"
        name="description"
        rows="6"
        :disabled="isCurrentUserClientCollaborator"
        placeholder="Enter description"
        v-model="description"></textarea>
      </div>
    </label>
    <div class="create-event__date-wrapper">
      <base-date-picker-button name="date"
                               class="create-event__date"
                               :class="{'create-event__date_error': isDateError}"
                               v-model="startDate"
                               :disabled="isCurrentUserClientCollaborator"
                               :maxDate="endDate">
        <div class="create-event__date-title">Start Date</div>
      </base-date-picker-button>
      <base-date-picker-button class="create-event__date"
                               v-model="endDate"
                               :minDate="startDate"
                               :disabled="isCurrentUserClientCollaborator">
        <div class="create-event__date-title">End Date</div>
      </base-date-picker-button>
      <div class="create-event__delete-wrapper" v-if="!isCurrentUserClientCollaborator">
        <base-material-design-icon class="create-event__delete" icon="delete"
                                   @click.stop="onDelete"
                                   v-if="isEditPopup"/>
      </div>

    </div>
    <div class="create-event__controls">
      <button class="create-event__button create-event__button_cancel"
              @click.prevent="close()">Cancel
      </button>
      <button class="create-event__button create-event__button_submit"
              v-if="!isCurrentUserClientCollaborator"
              @click="apply">
        {{submitButtonTitle}}
      </button>
    </div>
  </div>
</template>

<script>
import BaseModalMixins from '@/common/BaseModalMixins'
import moment from 'moment'
/**
 * Popup for creating/editing event
 */
export default {
  name: 'CreateEventModal',
  mixins: [BaseModalMixins],
  props: {
    event: {
      default: null
    }
  },
  data () {
    return {
      name: '',
      description: '',
      startDate: null,
      endDate: null,
      isValidating: false
    }
  },
  watch: {
    event: {
      handler (value) {
        if (value) {
          this.name = value.name
          this.startDate = moment(value.start_date).toDate()
          this.endDate = moment(value.end_date).toDate()
          this.description = value.description
        }
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    isFormInvalid () {
      return this.isDateError || this.isNameError
    },
    isCreatePopup () {
      return !this.event
    },
    isDateError () {
      return this.isValidating && !this.startDate
    },
    isNameError () {
      return this.isValidating && !this.name
    },
    isEditPopup () {
      return Boolean(this.event)
    },
    submitButtonTitle () {
      return this.isCreatePopup ? 'Create' : 'Update'
    },
    popupTitle () {
      return !this.isEditPopup ? 'Create New Event' : this.isCurrentUserClientCollaborator ? 'Event' : 'Edit Event'
    }
  },
  methods: {
    apply () {
      this.isValidating = true
      if (this.isFormInvalid) {
        return
      }
      const event = {
        name: this.name,
        description: this.description,
        start_date: this.startDate,
        end_date: this.endDate ? this.endDate : this.startDate
      }
      if (this.isEditPopup) {
        event._id = this.event._id
      }
      this.$emit('apply', event)
    },
    close () {
      this.$emit('cancel')
    },
    onDelete () {
      this.$emit('delete', this.event)
    }
  }
}
</script>

<style scoped lang="scss">

  .form-control {
    height: 50px;
    padding: 2px 20px;
    border: 2px solid $rfx-light-steel;
    outline: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 30px;
    color: $rfx-dark-march;

    &::placeholder {
      color: $rfx-pearl;
      font-weight: normal;
      font-size: 14px;
    }

    &:disabled {
      background-color: $rfx-light-steel;
    }
  }

  .error {
    &__message-description,
    &__message {
      color: $rfx-red;
      position: absolute;
      top: 80px;
      font-size: 10px;
      font-weight: normal;
    }

    &__message-description {
      top: 154px;
      left: 0;
    }
  }

  .create-event {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 30px;

    &__title {
      text-align: center;
      font-size: 34px;
      font-weight: 500;
      margin: 9px 0 35px;
      color: $rfx-dark-march;
    }

    &__form-group {
      display: flex;
      flex-direction: column;
      position: relative;

      &_invalid[type='text'] {
        border: 1px solid $rfx-red;
        border-radius: 4px;
      }
    }

    &__label {
      display: block;
      font-weight: 600;
      margin-bottom: 5px;
      color: $rfx-dark-march;
    }

    &__controls {
      display: flex;
      justify-content: flex-end;
      margin-top: 14px;
    }

    &__date-wrapper {
      display: flex;
      font-weight: bold;
    }

    &__date {
      flex: 0 0 30%;
      color: $rfx-dark-march;

      &_error {
        color: $rfx-red;
      }
    }

    &__date-title {
      color: currentColor;
    }

    &__delete-wrapper {
      flex: 1 1;
      display: flex;
      justify-content: flex-end;

    }

    &__delete {
      font-size: 24px;
      color: $rfx-dark-march;
      cursor: pointer;
      align-self: flex-start;

      &:hover {
        color: $rfx-bright-blue;
      }
    }

    &__button {
      width: 100px;
      height: 40px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      margin-left: 30px;
      border: none;
      outline: none;
      cursor: pointer;

      &_submit {
        background-color: $rfx-medium-march;
        color: #fff;

        &:disabled {
          pointer-events: none;
          background-color: #fff;
          color: $rfx-neutral-grey;
          cursor: default;
        }
      }

      &_disabled {
        pointer-events: none;
        background-color: #fff;
        color: $rfx-neutral-grey;
        cursor: default;
      }

      &_cancel {
        color: $rfx-dark-march;
        background-color: #fff;
      }
    }

    &__description {
      width: 100%;
      padding: 0;
      resize: none;
      outline: none;
      border: none;
      font-size: 14px;
      font-weight: 600;

      &::placeholder {
        color: $rfx-medium-grey;
        font-weight: normal;
        font-size: 14px;
      }

      &_invalid {
        border: 1px solid $rfx-red;
        border-radius: 4px;
      }

      &::placeholder {
        color: $rfx-pearl;
        font-weight: normal;
        font-size: 14px;
      }

      &__wrapper {
        padding: 10px 20px;
        height: auto;
      }
    }
  }

</style>
